import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ExternalLink, Save } from "lucide-react";

function Field({ label, value, onChange, textarea = false, testid }) {
  const Cmp = textarea ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">{label}</Label>
      <Cmp value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-white/5 border-white/15 text-white mt-1.5" />
    </div>
  );
}

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/landing").then((r) => setC(r.data)); }, []);

  const set = (k) => (v) => setC((p) => ({ ...p, [k]: v }));
  const setStat = (i, k, v) => setC((p) => ({ ...p, stats: p.stats.map((s, ix) => (ix === i ? { ...s, [k]: v } : s)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/landing", c);
      toast.success("Home page updated");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg || "Validation error" : d || "Save failed");
    } finally { setSaving(false); }
  };

  if (!c) return <AdminLayout title="Landing Page"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></AdminLayout>;

  return (
    <AdminLayout title="Landing Page" actions={
      <>
        <Button variant="ghost" onClick={() => window.open("/", "_blank")} className="text-white/70" data-testid="view-landing">
          <ExternalLink className="w-4 h-4 mr-1" /> View home page
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="save-content">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </>
    }>
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-white/4 border border-white/10 rounded-lg p-6 space-y-4">
          <div className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--accent))]">// hero headline</div>
          <Field label="Tagline" value={c.tagline} onChange={set("tagline")} testid="content-tagline" />
          <Field label="Headline line 1" value={c.hero_line1} onChange={set("hero_line1")} testid="content-hero-line1" />
          <Field label="Headline line 2" value={c.hero_line2} onChange={set("hero_line2")} testid="content-hero-line2" />
          <Field label="Headline highlight (yellow)" value={c.hero_accent} onChange={set("hero_accent")} testid="content-hero-accent" />
          <Field label="Subline" value={c.hero_subline} onChange={set("hero_subline")} textarea testid="content-hero-subline" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Hero stat value" value={c.hero_stat_value} onChange={set("hero_stat_value")} testid="content-hero-stat-value" />
            <Field label="Hero stat label" value={c.hero_stat_label} onChange={set("hero_stat_label")} testid="content-hero-stat-label" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white/4 border border-white/10 rounded-lg p-6 space-y-4">
            <div className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--accent))]">// portal stats</div>
            {c.stats.map((s, i) => (
              <div key={i} className="grid grid-cols-[110px_1fr] gap-4">
                <Field label={`Stat ${i + 1} value`} value={s.value} onChange={(v) => setStat(i, "value", v)} testid={`content-stat-${i}-value`} />
                <Field label="Label" value={s.label} onChange={(v) => setStat(i, "label", v)} testid={`content-stat-${i}-label`} />
              </div>
            ))}
          </div>

          <div className="bg-white/4 border border-white/10 rounded-lg p-6 space-y-4">
            <div className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--accent))]">// contact details</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone" value={c.phone} onChange={set("phone")} testid="content-phone" />
              <Field label="Email" value={c.email} onChange={set("email")} testid="content-email" />
            </div>
            <Field label="Website" value={c.website} onChange={set("website")} testid="content-website" />
            <Field label="Address (line break allowed)" value={c.address} onChange={set("address")} textarea testid="content-address" />
          </div>
        </div>
      </div>
      <p className="text-xs text-white/40 mt-6 max-w-5xl">
        Changes appear on the public home page immediately after saving. Leave a field as-is to keep the current text.
      </p>
    </AdminLayout>
  );
}
